import { Box, Grid, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import {
  BUSINESS,
  BUSINESS_LABEL,
  COST_SUMMARY_KINDS,
  COST_SUMMARY_TYPES,
} from 'pages/admin/CostDashboard/constants';
import CostAnalysisFilter from 'pages/admin/CostDashboard/CostAnalysisFilter';
import CostDetailsFilter from 'pages/admin/CostDashboard/CostDetailsFilter';
import { AllBusinessCostSummaryFilter } from 'pages/admin/CostDashboard/hooks/useGetAllBusinessSummaries';
import useGetBusinessCostSummary from 'pages/admin/CostDashboard/hooks/useGetBusinessCostSummary';
import useGetPeriodicCostRecords, {
  IUseGetPeriodicCostRecords,
} from 'pages/admin/CostDashboard/hooks/useGetPeriodicCostRecords';
import {
  BusinessResourceTable,
  CostBarChart,
  CostPieChart,
  PeriodicCostMetric,
  ResourceRankingTable,
} from 'pages/admin/CostDashboard/metrics';
import { ResourceRankingFilter } from 'pages/admin/CostDashboard/metrics/ResourceRankingTable';
import { useEffect, useMemo, useState } from 'react';
import { MetricGridItem } from 'shared/components/metric';
import { MetricWidget } from 'shared/components/metric/MetricWidget';
import { Section } from 'shared/components/Section';
import { commify } from 'shared/utils/strings';

import { IUseGetCostSummary } from './hooks/useGetCostSummary';
import CostSummaryFilter from './CostSummaryFilter';
import {
  useGetAllBusinessSummaries,
  useGetAllCostSummaries,
  useGetCostSummary,
  useGetRollupNamespaces,
} from './hooks';

const CostDashboard: React.FC = () => {
  const { data: namespaceList } = useGetRollupNamespaces();

  const [costSummaryFilterProps, setCostSummaryFilterProps] = useState<IUseGetCostSummary>({
    startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
    endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
    kind: 'ALL',
    type: 'ALL',
    area: 'rollup',
  });

  const [businessCostAnalysisFilter, setBusinessCostAnalysisFilter] =
    useState<AllBusinessCostSummaryFilter>({
      startTime: dayjs().startOf('month').format('YYYY-MM-DD'),
      endTime: dayjs().endOf('month').format('YYYY-MM-DD'),
      namespace: '',
    });

  const [resourceRankingFilter, setResourceRankingFilter] = useState<ResourceRankingFilter>({
    business: BUSINESS.ROLLUP_COMPONENTS,
    kind: 'ALL',
    type: 'ALL',
    subResource: 'ALL',
  });

  const [periodicCostRecords, setPeriodicCostRecords] = useState<IUseGetPeriodicCostRecords>({
    startTime: dayjs().startOf('month').format('YYYY-MM-DD'),
    endTime: dayjs().endOf('month').format('YYYY-MM-DD'),
    namespace: '',
    kind: COST_SUMMARY_KINDS.SERVICE,
    type: COST_SUMMARY_TYPES.COMPUTE,
    subresource: 'ALL',
    billingUnitId: undefined,
    resourceName: 'ALL',
  });

  const [barChartPage, setBarChartPage] = useState(1);
  const barChartItemsPerPage = 10;

  useEffect(() => {
    if (namespaceList?.namespace?.length) {
      setBusinessCostAnalysisFilter(prev => ({
        ...prev,
        namespace: namespaceList.namespace[0],
      }));

      setPeriodicCostRecords(prev => ({
        ...prev,
        namespace: namespaceList.namespace[0],
      }));
    }
  }, [namespaceList]);

  const { data: costSummary } = useGetCostSummary(costSummaryFilterProps);
  const { data: costSummaries, isLoading: isLoadingCostSummaries } =
    useGetAllCostSummaries(costSummaryFilterProps);

  const { data: periodicCostData, isLoading: isLoadingPeriodicCostRecords } =
    useGetPeriodicCostRecords(periodicCostRecords);

  const { data: businessCostSummaries, isLoading: isLoadingBusinessCostSummaries } =
    useGetAllBusinessSummaries({
      startTime: businessCostAnalysisFilter.startTime,
      endTime: businessCostAnalysisFilter.endTime,
      namespace: businessCostAnalysisFilter.namespace,
    });

  const { data: businessCostSummary } = useGetBusinessCostSummary({
    startTime: businessCostAnalysisFilter.startTime,
    endTime: businessCostAnalysisFilter.endTime,
    namespace: businessCostAnalysisFilter.namespace,
    business: resourceRankingFilter.business,
    kind: resourceRankingFilter.kind,
    type: resourceRankingFilter.type,
    subResource: resourceRankingFilter.subResource,
  });

  const totalMonthCost = useMemo(() => {
    const sum = costSummary?.items?.reduce((sum, item) => sum + item.totalCost, 0) ?? 0;

    return Math.round(sum);
  }, [costSummary]);

  const avgCostPerRollup = useMemo(() => {
    const namespaceCount = namespaceList?.namespace?.length ?? 0;

    if (namespaceCount === 0) return 0;

    return Math.round(totalMonthCost / namespaceCount);
  }, [totalMonthCost, namespaceList]);

  const businessCostPieChartData = useMemo(() => {
    return businessCostSummaries
      .filter(entry => (entry?.data?.items?.length ?? 0) > 0)
      .map(entry => ({
        name: entry.businessKey,
        value: entry?.data?.items.reduce((sum, item) => sum + (item.costCounter ?? 0), 0),
      }));
  }, [businessCostSummaries]);

  const namespaceCostPieChartData = useMemo(() => {
    return costSummaries
      .filter(entry => (entry?.data?.items?.length ?? 0) > 0)
      .map(entry => ({
        name: entry.namespace,
        value: entry?.data?.items.reduce((sum, item) => sum + (item.totalCost ?? 0), 0),
      }));
  }, [costSummaries]);

  const highestMonthlyCost = useMemo(() => {
    const max = namespaceCostPieChartData.reduce((acc, entry) => {
      return Math.max(acc, entry.value ?? 0);
    }, 0);

    return Math.round(max);
  }, [namespaceCostPieChartData]);

  const namespaceCostBarChartData = useMemo(() => {
    return namespaceCostPieChartData
      .map(item => ({
        name: item.name,
        value: item.value ?? 0,
      }))
      .sort((a, b) => b.value - a.value);
  }, [namespaceCostPieChartData]);

  const paginatedNamespaceCostBarChartData = useMemo(() => {
    const sorted = namespaceCostPieChartData
      .map(item => ({
        name: item.name,
        value: item.value ?? 0,
      }))
      .sort((a, b) => b.value - a.value);

    const start = (barChartPage - 1) * barChartItemsPerPage;
    const end = start + barChartItemsPerPage;

    return sorted.slice(start, end);
  }, [namespaceCostPieChartData, barChartPage]);

  const costOverviewMetrics = useMemo(() => {
    return [
      {
        id: 1,
        title: 'Total Monthly Cost',
        value: `$${commify(totalMonthCost)}`,
      },
      {
        id: 2,
        title: 'Active Rollups',
        value: namespaceList?.namespace?.length,
      },
      {
        id: 3,
        title: 'Avg Cost per Rollup',
        value: `$${commify(avgCostPerRollup)}`,
      },
      {
        id: 4,
        title: 'Highest Rollup Cost',
        value: `$${commify(highestMonthlyCost)}`,
      },
    ];
  }, [namespaceList, totalMonthCost, avgCostPerRollup, highestMonthlyCost]);

  return (
    <>
      <Section
        renderContent={
          <Grid container>
            {costOverviewMetrics.map(({ id, title, value }) => {
              return <MetricWidget key={id} label={title} value={value} xs={3} />;
            })}
          </Grid>
        }
        title="Overview"
      />

      <Section
        renderContent={
          <Stack spacing={5} sx={{ p: 3 }}>
            <CostSummaryFilter
              {...costSummaryFilterProps}
              setCostFilterProps={setCostSummaryFilterProps}
            />

            <CostPieChart
              data={namespaceCostPieChartData}
              dataKey="value"
              height={320}
              isLoading={isLoadingCostSummaries}
              nameKey="name"
              title="Namespace Cost Distribution"
            />

            <CostBarChart
              data={paginatedNamespaceCostBarChartData}
              hasPagination
              isLoading={isLoadingCostSummaries}
              page={barChartPage}
              pageSize={barChartItemsPerPage}
              setPage={setBarChartPage}
              title="Namespace Cost Ranking"
              totalCount={namespaceCostBarChartData.length}
              unit="currency"
            />
          </Stack>
        }
        title="Cost Summary"
      />

      <Section
        renderContent={
          <Stack spacing={5} sx={{ p: 3 }}>
            <CostAnalysisFilter
              {...businessCostAnalysisFilter}
              setBusinessCostAnalysisFilter={setBusinessCostAnalysisFilter}
            />

            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <CostPieChart
                  data={businessCostPieChartData}
                  dataKey="value"
                  isLoading={isLoadingBusinessCostSummaries}
                  nameKey="name"
                  title="Business Cost Distribution"
                />

                <BusinessResourceTable businessCostSummaries={businessCostSummaries} />
              </Stack>

              <Box
                sx={{
                  backgroundColor: '#fff',
                  borderRadius: 2,
                  boxShadow: 1,
                  p: 3,
                  width: '100%',
                }}
              >
                <Typography mb={2} variant="h6">
                  {`Resource Ranking in ${BUSINESS_LABEL[resourceRankingFilter.business]}`}
                </Typography>

                <ResourceRankingTable
                  {...resourceRankingFilter}
                  businessCostSummary={businessCostSummary}
                  setResourceRankingFilter={setResourceRankingFilter}
                />
              </Box>
            </Stack>
          </Stack>
        }
        title="Business Cost Analysis"
      />

      <Section
        renderContent={
          <Stack spacing={5} sx={{ p: 3 }}>
            <CostDetailsFilter
              {...periodicCostRecords}
              setCostDetailsFilter={setPeriodicCostRecords}
            />
            <Grid container>
              <MetricGridItem item xs={12}>
                <PeriodicCostMetric
                  isLoading={isLoadingPeriodicCostRecords}
                  periodicCostData={periodicCostData}
                />
              </MetricGridItem>
            </Grid>
          </Stack>
        }
        title="Cost Details"
      />
    </>
  );
};

export default CostDashboard;
